import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Plus, Minus, RotateCw, X, Search, Folder, ChevronDown, ChevronRight, Star, BarChart2, User, GripVertical } from 'lucide-react';
import './App.css';
import Profile from './components/Profile';
import Analytics from './components/Analytics';
import CompleteChoreModal from './components/CompleteChoreModal';
import ChoreLogDetailsModal from './components/ChoreLogDetailsModal';
import ChoreItem from './components/ChoreItem';
import EditChoreModal from './components/EditChoreModal';
import HistoryFeed from './components/HistoryFeed';
import Auth from './components/Auth';
import HouseholdBanner from './components/HouseholdBanner';
import Avatar from './components/Avatar';
import ScoreboardStrip from './components/ScoreboardStrip';
import ArchiveChoreSheet from './components/ArchiveChoreSheet';
import ChoresSkeleton from './components/ChoresSkeleton';
import EmptyChores from './components/EmptyChores';
import WelcomeCarousel from './components/onboarding/WelcomeCarousel';
import TourCarousel from './components/onboarding/TourCarousel';
import useChores from './hooks/useChores';
import useWeeklyScoreboardStrip from './hooks/useWeeklyScoreboardStrip';
import { ptsFor } from './utils/points';

function App() {
  const [newChore, setNewChore] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [view, setView] = useState('chores'); // 'chores' or 'profile'
  const [activeChore, setActiveChore] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [archivingChore, setArchivingChore] = useState(null);

  const [isAddingChore, setIsAddingChore] = useState(false);
  const [newChoreDifficulty, setNewChoreDifficulty] = useState(2);
  const [newChoreMinutes, setNewChoreMinutes] = useState(10);
  const [newChoreFolder, setNewChoreFolder] = useState('General');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [editingChore, setEditingChore] = useState(null);
  const [showTabs, setShowTabs] = useState(true);
  const scrollRef = React.useRef(null);
  const [folderOrder, setFolderOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('folderOrder')) || []; } catch { return []; }
  });
  const draggingFolderRef = React.useRef(null);
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);
  const [customFolderName, setCustomFolderName] = useState('');

  // Extract user info from token
  const currentUserId = token ? JSON.parse(atob(token.split('.')[1])).userId : null;
  const hasHousehold = token && JSON.parse(atob(token.split('.')[1])).householdId;

  const {
    chores, logs, roommates, userData, loading,
    fetchChores, fetchLogs, fetchUserData,
    addChore, archiveChore, purgeChore, toggleFavorite, updateChore,
    completeChore, gradeLog, editLog, deleteLog, editGrade, markOnboardingSeen,
  } = useChores(token, hasHousehold);

  const { standings: weeklyStandings, total: weeklyTotal, refetch: refetchWeeklyStrip } = useWeeklyScoreboardStrip(token, hasHousehold);

  const existingFolders = useMemo(() => {
    return Array.from(new Set(chores.map(c => (c.folder || 'General').trim())))
      .filter((v, i, a) => a.findIndex(t => t.toLowerCase() === v.toLowerCase()) === i)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'accent' }));
  }, [chores]);

  // ─── Back Button Handling ───
  useEffect(() => {
    // Push state when view changes
    const state = { view };
    if (window.history.state?.view !== view) {
      window.history.pushState(state, '', '');
    }

    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        setView(event.state.view);
      } else {
        setView('chores');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [view]);

  const lastScrollYRef = React.useRef(0);

  // ─── Scroll-Aware Tabs ───
  useEffect(() => {
    const handleScroll = () => {
      const el = scrollRef.current;
      const currentScrollY = el ? el.scrollTop : window.scrollY;
      const scrollHeight = el ? el.scrollHeight : document.documentElement.scrollHeight;
      const clientHeight = el ? el.clientHeight : window.innerHeight;
      const lastScrollY = lastScrollYRef.current;

      // Check if we are at the very bottom
      const isAtBottom = currentScrollY + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setShowTabs(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        setShowTabs(false); // Scrolling down
      } else if (currentScrollY < lastScrollY - 5) {
        setShowTabs(true);  // Scrolling up
      }

      lastScrollYRef.current = currentScrollY;
    };

    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll, { passive: true });
    }
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      el?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleFolder = (folder) => {
    setExpandedFolders(prev => ({ ...prev, [folder.toLowerCase()]: !prev[folder.toLowerCase()] }));
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  // Builds the payload from the add-chore form state, then resets the form on success
  const handleAddChoreSubmit = async (e) => {
    e.preventDefault();
    const finalFolder = isCreatingNewFolder ? customFolderName : newChoreFolder;
    const success = await addChore({
      title: newChore,
      defaultDifficulty: newChoreDifficulty,
      defaultMinutes: newChoreMinutes,
      folder: finalFolder,
    });
    if (success) {
      setNewChore('');
      setNewChoreDifficulty(2);
      setNewChoreMinutes(10);
      setNewChoreFolder('General');
      setIsCreatingNewFolder(false);
      setCustomFolderName('');
      setIsAddingChore(false);
    }
  };

  const handleUpdateChore = async (id, data) => {
    if (await updateChore(id, data)) setEditingChore(null);
  };

  const onCompleteSubmit = async (logData) => {
    // logData contains helperIds, actualMinutes, etc.
    await completeChore(activeChore.id, logData);
    refetchWeeklyStrip();
    setActiveChore(null); // Close the modal
  };

  const handleGradeSubmit = async (logId, gradeData) => {
    if (await gradeLog(logId, gradeData)) setSelectedLog(null);
  };

  const handleEditLog = async (logId, logData) => {
    if (await editLog(logId, logData)) setSelectedLog(null);
  };

  const handleDeleteLog = async (logId) => {
    if (await deleteLog(logId)) setSelectedLog(null);
  };

  const handleEditGrade = async (logId, gradeData) => {
    if (await editGrade(logId, gradeData)) setSelectedLog(null);
  };

  if (!token) return <Auth setToken={setToken} />;

  return (
    <div className="max-w-md mx-auto md:my-10 bg-ink-shell md:rounded-3xl shadow-none md:shadow-2xl overflow-hidden flex flex-col h-[100dvh] md:h-[85vh] relative">
      {!hasHousehold && userData && !userData.hasSeenWelcome && (
        <WelcomeCarousel
          onDone={() => { markOnboardingSeen('hasSeenWelcome'); setView('profile'); }}
          onSkip={() => markOnboardingSeen('hasSeenWelcome')}
        />
      )}
      {hasHousehold && userData && !userData.hasSeenTour && (
        <TourCarousel
          onDone={() => markOnboardingSeen('hasSeenTour')}
          onSkip={() => markOnboardingSeen('hasSeenTour')}
        />
      )}

      {/* App Content */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-6 flex flex-col"
        style={{
          paddingTop: 'max(1.5rem, calc(env(safe-area-inset-top) + 0.5rem))',
          paddingBottom: 'calc(6.5rem + env(safe-area-inset-bottom))',
        }}
      >
        {/* 1. Header — Chores/Analytics get their title from the shared household banner instead */}
        {view === 'profile' && (
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-serif text-ink-fg tracking-tight">My Profile</h1>
          </div>
        )}

        {/* 2. The Switch */}
        {view === 'profile' ? (
          <Profile
            token={token}
            setToken={setToken}
            roommates={roommates}
            onLogout={logout}
            onUpdate={() => {
              fetchChores();
              fetchLogs();
              fetchUserData();
            }}
          />
        ) : view === 'analytics' ? (
          <Analytics
            currentUserId={currentUserId}
            onLogClick={setSelectedLog}
            household={userData?.household}
            roommates={roommates}
            onGoToChores={() => setView('chores')}
          />
        ) : (
          <div className="flex flex-col flex-1">
            {!hasHousehold && (
              <div className="bg-mint/10 border border-mint/25 rounded-2xl p-4 mb-6 shadow-sm">
                <p className="text-sm text-ink-fg/80 flex items-center">
                  <strong className="mr-2 text-ink-fg">Wait!</strong> You need a household.
                  <button onClick={() => setView('profile')} className="ml-auto underline font-bold text-mint hover:text-ink-fg">Go to Profile</button>
                </p>
              </div>
            )}

            {hasHousehold && userData?.household && (
              <>
                <HouseholdBanner
                  household={userData.household}
                  height={220}
                  eyebrow="HOUSEHOLD"
                  heading={userData.household.name}
                  memberColors={roommates.map(r => r.color).filter(Boolean)}
                  rightSlot={<Avatar user={userData} size="w-8 h-8" textSize="text-sm" ring="border-2 border-ink-banner/25" />}
                />
                <ScoreboardStrip standings={weeklyStandings} total={weeklyTotal} />
              </>
            )}

            <div className={`transition-opacity duration-300 flex-1 flex flex-col ${hasHousehold ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>

              {/* Search Bar, Favorites Toggle & Refresh */}
              <div className="mb-4 mt-2 flex items-center gap-3">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-ink-fg/35" />
                  </div>
                  <input
                    type="text"
                    className="w-full bg-ink-card2 border border-white/10 text-ink-fg text-sm rounded-xl pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-mint focus:border-transparent outline-none transition-all placeholder:text-ink-fg/35"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                  className={`p-2.5 rounded-xl border transition-colors flex items-center justify-center ${showFavoritesOnly ? 'bg-amber-400/10 border-amber-400/30 text-amber-400 shadow-sm' : 'bg-ink-card2 border-white/10 text-ink-fg/35 hover:bg-ink-card3'}`}
                  title={showFavoritesOnly ? "Showing Favorites" : "Show Favorites Only"}
                >
                  <Star size={18} className={showFavoritesOnly ? 'fill-amber-400' : ''} />
                </button>
                <button
                  onClick={() => { fetchChores(); fetchLogs(); refetchWeeklyStrip(); }}
                  className="p-2.5 rounded-xl border border-white/10 bg-ink-card2 text-ink-fg/40 hover:text-mint hover:bg-mint/10 transition-colors flex items-center justify-center"
                  title="Refresh"
                >
                  <RotateCw
                    size={18}
                    className={loading ? 'animate-spin text-mint' : ''}
                  />
                </button>
              </div>

              <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading && chores.length === 0 ? (
                  <ChoresSkeleton />
                ) : !loading && chores.length === 0 && hasHousehold ? (
                  <EmptyChores
                    memberCount={roommates.length}
                    onAddClick={() => setIsAddingChore(true)}
                    onPickStarter={(s) => addChore({
                      title: s.name,
                      defaultMinutes: s.minutes,
                      defaultDifficulty: s.difficulty,
                      folder: 'General',
                    })}
                  />
                ) : (() => {
                  const grouped = chores
                    .filter(c => showFavoritesOnly ? c.isFavorite : true)
                    .filter(c => c.title.toLowerCase().includes(searchQuery.toLowerCase()))
                    .reduce((groups, chore) => {
                      const rawFolder = (chore.folder || 'General').trim();
                      // Find the canonical folder name from existingFolders that matches case-insensitively
                      const folder = existingFolders.find(f => f.toLowerCase() === rawFolder.toLowerCase()) || rawFolder;

                      if (!groups[folder]) groups[folder] = [];
                      groups[folder].push(chore);
                      return groups;
                    }, {});

                  // Sort folders by saved order; new folders go to the end
                  const allFolderNames = Object.keys(grouped);
                  const ordered = [
                    ...folderOrder.map(f => allFolderNames.find(af => af.toLowerCase() === f.toLowerCase())).filter(Boolean),
                    ...allFolderNames.filter(f => !folderOrder.some(fo => fo.toLowerCase() === f.toLowerCase()))
                  ];

                  const handleDragStart = (folder) => {
                    draggingFolderRef.current = folder;
                  };
                  const handleDragOver = (e) => e.preventDefault();
                  const handleDrop = (targetFolder) => {
                    const from = draggingFolderRef.current;
                    if (!from || from === targetFolder) return;
                    const next = [...ordered];
                    const fromIdx = next.indexOf(from);
                    const toIdx = next.indexOf(targetFolder);
                    next.splice(fromIdx, 1);
                    next.splice(toIdx, 0, from);
                    setFolderOrder(next);
                    localStorage.setItem('folderOrder', JSON.stringify(next));
                    draggingFolderRef.current = null;
                  };

                  return ordered.map(folder => {
                    const folderChores = grouped[folder];
                    const isExpanded = searchQuery.length > 0 || expandedFolders[folder.toLowerCase()];
                    return (
                      <div
                        key={folder}
                        className="space-y-2"
                        draggable
                        onDragStart={() => handleDragStart(folder)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(folder)}
                      >
                        <button
                          onClick={() => toggleFolder(folder)}
                          className="w-full text-left text-xs font-mono font-medium text-ink-fg/55 hover:text-mint uppercase tracking-wider flex items-center justify-between p-2 hover:bg-ink-card2 rounded-lg transition-colors group cursor-grab active:cursor-grabbing"
                        >
                          <span className="flex items-center gap-1.5 min-w-0">
                            <GripVertical size={13} className="text-ink-fg/25 group-hover:text-ink-fg/40 flex-shrink-0 transition-colors" />
                            <Folder size={14} className="group-hover:text-mint transition-colors flex-shrink-0" />
                            <span className="truncate">{folder}</span>
                          </span>
                          <span className="flex items-center gap-2 flex-shrink-0 pl-2">
                            <span className="normal-case text-ink-fg/35 group-hover:text-mint/70 transition-colors">
                              {folderChores.length} {folderChores.length === 1 ? 'chore' : 'chores'} · {folderChores.reduce((sum, c) => sum + ptsFor(c.defaultMinutes, c.defaultDifficulty), 0)} pts
                            </span>
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="space-y-1 animate-in fade-in slide-in-from-top-1">
                            {folderChores.map(chore => (
                              <ChoreItem
                                key={chore.id}
                                chore={chore}
                                onComplete={() => setActiveChore(chore)}
                                onDelete={() => setArchivingChore(chore)}
                                onToggleFavorite={toggleFavorite}
                                onEdit={setEditingChore}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {isAddingChore ? (
                <div className="mt-6 mb-2 bg-ink-card p-5 rounded-2xl border border-white/10 shadow-sm animate-in fade-in slide-in-from-bottom-2">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-serif text-lg text-ink-fg">Add New Chore</h3>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1.5 rounded-lg bg-mint/15 text-mint text-[11px] font-mono font-semibold">{ptsFor(newChoreMinutes, newChoreDifficulty)} PTS</span>
                      <button onClick={() => setIsAddingChore(false)} className="text-ink-fg/40 hover:text-ink-fg/70 bg-ink-card2 hover:bg-ink-card3 rounded-full p-1.5 transition-colors">
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                  <form onSubmit={handleAddChoreSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-1">Chore Name</label>
                      <input
                        className="w-full bg-ink-card2 border border-white/10 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none placeholder:text-ink-fg/30 font-medium"
                        value={newChore}
                        onChange={(e) => setNewChore(e.target.value)}
                        placeholder="e.g., Take out trash"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-1 flex items-center gap-1">
                        Folder / Category
                      </label>
                      <select
                        className="w-full bg-ink-card2 border border-white/10 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none mb-2"
                        value={isCreatingNewFolder ? "__new__" : newChoreFolder}
                        onChange={(e) => {
                          if (e.target.value === "__new__") {
                            setIsCreatingNewFolder(true);
                          } else {
                            setIsCreatingNewFolder(false);
                            setNewChoreFolder(e.target.value);
                          }
                        }}
                      >
                        {existingFolders.map(f => <option key={f} value={f}>{f}</option>)}
                        <option value="__new__">+ New Folder...</option>
                      </select>
                      {isCreatingNewFolder && (
                        <input
                          className="w-full bg-mint/10 border border-mint/30 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none placeholder:text-ink-fg/30 animate-in fade-in slide-in-from-top-1"
                          value={customFolderName}
                          onChange={(e) => setCustomFolderName(e.target.value)}
                          placeholder="Enter new folder name..."
                          autoFocus
                          required
                        />
                      )}
                    </div>
                    <div className="bg-ink-card3 p-4 rounded-2xl border border-white/10 space-y-5">
                      <div>
                        <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Default Time</label>
                        <div className="flex items-center justify-between">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-3xl font-serif text-ink-fg">{newChoreMinutes}</span>
                            <span className="text-xs font-mono text-ink-fg/40 uppercase">min</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setNewChoreMinutes(m => Math.max(1, parseInt(m || 0) - 5))}
                              className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                            >
                              <Minus size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewChoreMinutes(m => parseInt(m || 0) + 5)}
                              className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Default Difficulty</label>
                        <div className="flex gap-1.5">
                          {[1, 2, 3, 4, 5].map(n => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setNewChoreDifficulty(n)}
                              className={`flex-1 h-9 rounded-xl text-sm font-bold transition-all ${n <= newChoreDifficulty
                                ? 'bg-mint text-ink-shell'
                                : 'bg-ink-card2 text-ink-fg/35 border border-white/10 hover:border-mint/25'
                                }`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-mint hover:brightness-95 text-ink-shell p-3 rounded-xl shadow-md transition-all font-bold mt-2"
                    >
                      Add Chore
                    </button>
                  </form>
                </div>
              ) : (
                <div className="mt-6 mb-2 flex justify-center">
                  <button
                    onClick={() => setIsAddingChore(true)}
                    className="bg-mint/15 hover:bg-mint/25 text-mint p-3 rounded-full shadow-sm transition-all transform active:scale-95 flex items-center gap-2 font-bold px-6 border border-mint/25"
                  >
                    <Plus size={20} /> Add Chore
                  </button>
                </div>
              )}

              <HistoryFeed logs={logs} onSelectLog={setSelectedLog} />
            </div>

            {activeChore && (
              <CompleteChoreModal
                chore={activeChore}
                roommates={roommates}
                currentUser={userData}
                onClose={() => setActiveChore(null)}
                onSubmit={onCompleteSubmit}
              />
            )}

            {editingChore && (
              <EditChoreModal
                chore={editingChore}
                existingFolders={existingFolders}
                onClose={() => setEditingChore(null)}
                onSave={handleUpdateChore}
              />
            )}

            {archivingChore && (
              <ArchiveChoreSheet
                chore={archivingChore}
                onClose={() => setArchivingChore(null)}
                onArchive={async (id) => {
                  if (await archiveChore(id)) setArchivingChore(null);
                }}
                onPurge={async (id) => {
                  if (await purgeChore(id)) setArchivingChore(null);
                }}
              />
            )}

            {selectedLog && (
              <ChoreLogDetailsModal
                log={selectedLog}
                currentUserId={currentUserId}
                onClose={() => setSelectedLog(null)}
                onGradeSubmit={handleGradeSubmit}
                onEditLog={handleEditLog}
                onDeleteLog={handleDeleteLog}
                onEditGrade={handleEditGrade}
              />
            )}
          </div>
        )}
      </div>

      {/* Bottom Tab Bar */}
      <nav
        className={`fixed md:sticky bottom-0 left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:right-auto w-full max-w-md bg-ink-tabbar/95 backdrop-blur-sm border-t border-white/10 flex justify-around items-center pt-3 px-4 md:rounded-b-3xl shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.4)] z-40 transition-all duration-500 ease-in-out transform ${showTabs ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none md:translate-y-0 md:opacity-100 md:pointer-events-auto'
          }`}
        style={{ paddingBottom: 'max(0.75rem, calc(env(safe-area-inset-bottom) + 0.25rem))' }}
      >
        {[
          { id: 'chores', label: 'Chores', Icon: CheckCircle },
          { id: 'analytics', label: 'Analytics', Icon: BarChart2 },
          { id: 'profile', label: 'You', Icon: User },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`flex flex-col items-center gap-0.5 px-6 py-1.5 rounded-xl transition-all ${view === id ? 'text-mint' : 'text-ink-fg/40 hover:text-ink-fg/70'
              }`}
          >
            <Icon size={20} />
            <span className={`text-[10px] font-semibold ${view === id ? 'text-mint' : 'text-ink-fg/40'}`}>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}


export default App;